import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';
import ReactPlayer from 'react-player';

import useScreen from 'hooks/useScreen';
import { logError } from 'commons/logger';
import ReactVimeo from 'components/consumer/ReactVimeo';
import RatioContainer from 'components/consumer/RatioContainer';
import BrowserOnly from 'components/consumer/BrowserOnly';

const MEDIA_ERR_LABELS = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
};

const PDPProductIntroVideoWistia = ({ videoId, variant }) => {
  const [isThumbnailImageLoaded, setIsThumbnailImageLoaded] = useState(false);

  return (
    !!videoId && (
      <div
        // We use a custom className here to support overriding nested third
        // party styles in globals-tailwind.css
        className={classNames('pdp-wistia-video h-full flex items-center', {
          'w-full': variant === 'PDPDesktopFullScreenGallery2',
        })}
      >
        <Helmet>
          <script
            src={`//fast.wistia.com/embed/medias/${videoId}.jsonp`}
            async
          />

          <script src="//fast.wistia.com/assets/external/E-v1.js" async />
        </Helmet>

        <div className="wistia_responsive_padding">
          <div className="wistia_responsive_wrapper">
            <div
              className={`wistia_embed wistia_async_${videoId} videoFoam=true muted=true autoPlay={true} playbar=false qualityControl=false settingsControl=false smallPlayButton=false fullscreenButton=false silentAutoPlay=allow googleAnalytics=false endVideoBehavior=reset`}
            >
              <div
                className={classNames('wistia_swatch', {
                  wistia_swatch_img_loaded: !!isThumbnailImageLoaded,
                })}
              >
                <img
                  src={`//fast.wistia.com/embed/medias/${videoId}/swatch`}
                  alt=""
                  aria-hidden="true"
                  onLoad={() => {
                    setIsThumbnailImageLoaded(true);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

const PDPProductIntroVideoVimeo = ({ videoId, variant }) => {
  // If video URL was copied while logged into Vimeo, it may be passed into the
  // component in the format https://vimeo.com/manage/videos/560538232 but the
  // only format supported by the Vimeo player is https://vimeo.com/560538232
  const vimeoVideoId = videoId
    ? videoId.replace('/manage/videos', '')
    : videoId;

  return (
    !!videoId && (
      <div
        className={classNames('h-full flex items-center justify-center', {
          'w-full': variant === 'PDPDesktopFullScreenGallery2',
        })}
      >
        <RatioContainer ratio={9 / 16} wrapperClassName="flex-1">
          <ReactVimeo
            className="w-full h-full relative"
            video={vimeoVideoId}
            responsive
            autoplay
            showTitle={false}
          />
        </RatioContainer>
      </div>
    )
  );
};

const PDPProductIntroVideoPlayer = ({ videoId, variant }) => {
  const { isDesktop } = useScreen();
  const [hasError, setHasError] = useState(false);
  const playerRef = useRef(null);

  // Safari 26 dims the frame while its controls show, so they come on with the pointer.
  // Set on the element: the bundled react-player 0.24 ignores controls prop changes
  const showControls = useCallback(() => {
    const video = playerRef.current?.getInternalPlayer?.();

    if (!video) {
      return false;
    }

    video.controls = true;

    return true;
  }, []);

  // Keyboard users have no pointer to bring the controls in with
  useEffect(() => {
    if (!isDesktop) {
      return undefined;
    }

    const onKeyDown = () => {
      // not { once }: the player may still be mounting on the first keystroke
      if (showControls()) {
        document.removeEventListener('keydown', onKeyDown);
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDesktop, showControls]);

  if (hasError) {
    return null;
  }

  return (
    !!videoId && (
      <div
        className={classNames('h-full flex items-center justify-center', {
          'w-full': variant === 'PDPDesktopFullScreenGallery2',
        })}
      >
        <RatioContainer ratio={9 / 16} wrapperClassName="flex-1">
          <div
            className="w-full h-full relative"
            // The video can open under the cursor after a tile click, so
            // mouseenter may not fire. Mousemove also retries if the player
            // wasn't ready on the first hover.
            onMouseMove={isDesktop ? showControls : undefined}
          >
            <ReactPlayer
              ref={playerRef}
              url={videoId}
              playsinline
              // no onReady wait: that needs canplay, which Safari never fires
              // with preload="metadata"
              playing={isDesktop}
              // Mobile has no hover and no autoplay, so nothing else would show them
              controls={!isDesktop}
              loop
              muted
              width="100%"
              height="100%"
              config={{
                file: {
                  attributes: {
                    preload: 'metadata',
                  },
                },
              }}
              onError={() => {
                // react-player forwards the native <video> error event (a DOM Event,
                // not an Error). The old throw used error.message, which is always
                // undefined and produced the useless Sentry message "Unknown error".
                // Only act when the browser sets a MediaError code on the element.
                const mediaErrorCode = playerRef.current?.getInternalPlayer?.()
                  ?.error?.code;

                if (!mediaErrorCode) {
                  return;
                }

                const mediaErrorLabel =
                  MEDIA_ERR_LABELS[mediaErrorCode] ?? 'UNKNOWN';

                logError(
                  new Error(
                    `PDP intro video failed to play: ${mediaErrorLabel}`
                  ),
                  { mediaErrorCode: mediaErrorLabel },
                  { videoUrl: videoId }
                );

                setHasError(true);
              }}
            />
          </div>
        </RatioContainer>
      </div>
    )
  );
};

const PDPProductIntroVideo = ({ videoId, variant }) => {
  if (!videoId) {
    return null;
  }

  const isVimeoUrl = /vimeo\.com/i.test(videoId);

  return (
    <BrowserOnly>
      {videoId.startsWith('http') && !isVimeoUrl ? (
        <PDPProductIntroVideoPlayer videoId={videoId} variant={variant} />
      ) : isVimeoUrl || !/[a-z]/g.test(videoId) ? (
        <PDPProductIntroVideoVimeo videoId={videoId} variant={variant} />
      ) : (
        <PDPProductIntroVideoWistia videoId={videoId} variant={variant} />
      )}
    </BrowserOnly>
  );
};

PDPProductIntroVideoWistia.propTypes = {
  variant: PropTypes.oneOf(['PDPDesktopFullScreenGallery2']),
  videoId: PropTypes.string,
};

PDPProductIntroVideoVimeo.propTypes = {
  variant: PropTypes.oneOf(['PDPDesktopFullScreenGallery2']),
  videoId: PropTypes.string,
};

PDPProductIntroVideoPlayer.propTypes = {
  variant: PropTypes.oneOf(['PDPDesktopFullScreenGallery2']),
  videoId: PropTypes.string,
};

PDPProductIntroVideo.propTypes = {
  variant: PropTypes.oneOf(['PDPDesktopFullScreenGallery2']),
  videoId: PropTypes.string,
};

export default PDPProductIntroVideo;
